import {
  ASSISTANT_CHAT_GROUP_KEY_PREFIX,
  MULTI_ASSISTANT_CHAT_GROUP_KEY,
  MULTI_ASSISTANT_CHAT_GROUP_LABEL_KEY,
  UNKNOWN_ASSISTANT_CHAT_GROUP_KEY,
  UNKNOWN_ASSISTANT_CHAT_GROUP_LABEL_KEY,
} from "../constants/chat-assistant-group";
import type { IChatListObject } from "../types/chat";
import type {
  IChatAssistantGroup,
  IFormatChatAssistantGroupLabelProps,
  IGroupChatsByAssistantProps,
} from "../types/chat-assistant-group";
import { getChatActivityDate } from "./chat-date-group";

// NOTE: NSAI-5678 — the same definition the chat page uses for isAssistantChat:
// a chat is an assistant chat while its current selection holds an assistant.
// A payload from a server older than this feature has no `assistants` field, so
// every chat counts as a regular one there.
//
// NOTE: NSAI-5678 (review follow-up) — deliberately asymmetric with
// groupChatsByAssistant below, which files an empty `assistants: []` (an
// unresolvable — hard-deleted — assistant) into its own fallback group rather
// than treating it as absent. This function can't make that same distinction:
// it runs on chats from EITHER list, merged (e.g. the Pinned section), and an
// empty array from the *regular* list is the overwhelmingly common case — a
// chat with genuinely no assistant. Treating empty as "is an assistant chat"
// here would put a Bot indicator on every ordinary pinned chat to fix a
// missing indicator on the rare hard-deleted-assistant one; the false
// positive is worse than the false negative it would replace, so this stays
// conservative and only the fallback group itself loses the icon.
export const isAssistantChat = (chat: IChatListObject): boolean =>
  (chat.assistants?.length ?? 0) > 0;

// NOTE: Buckets assistant chats per assistant, most recently active assistant
// first, with the chats inside each bucket sorted by activity descending. Empty
// buckets are never emitted.
//
// NOTE: NSAI-5867 — a chat with MORE THAN ONE assistant gets its own bucket
// instead of filing under slot 0. Keying on the first assistant hid the fact
// that a conversation involved several, and put it among that assistant's own
// chats where nothing distinguished it. The chat still appears exactly once.
// Nothing else has to change for the second half of the report ("continuing with
// one assistant should re-file it under that assistant"): the group is derived
// from `chat.assistants` on every render, so as soon as the server narrows
// `current_assistant_ids` to one the chat moves to that assistant's bucket.
//
// An ABSENT `assistants` field and an EMPTY one mean different things here, and
// the difference is what keeps the section correct against an older server. A
// server that does not know the `kind` parameter ignores it and answers the
// assistant query with the user's whole history, every row without the field —
// those are skipped, so the section stays empty instead of duplicating the
// entire list under "unavailable assistant". A server that does know it only
// ever puts real assistant chats in that answer and always sets the field, so
// an empty array there means the chat's assistant could not be resolved (hard
// deleted) — that chat belongs in the fallback group, because nothing else
// would show it: the regular list excludes it server-side.
export const groupChatsByAssistant = ({
  chats,
}: IGroupChatsByAssistantProps): IChatAssistantGroup[] => {
  const groupsByKey = new Map<string, IChatAssistantGroup>();

  chats.forEach((chat) => {
    if (!chat.assistants) return;

    const activityDate = getChatActivityDate(chat);

    if (Number.isNaN(activityDate.getTime())) return;

    const isMultiAssistantChat = chat.assistants.length > 1;
    // NOTE: The multi-assistant bucket carries no single assistant, so it has no
    // name or avatar of its own — `formatChatAssistantGroupLabel` resolves its
    // label from the group key instead.
    const primaryAssistant = isMultiAssistantChat
      ? null
      : (chat.assistants[0] ?? null);

    const resolveKey = (): string => {
      if (isMultiAssistantChat) return MULTI_ASSISTANT_CHAT_GROUP_KEY;
      return primaryAssistant
        ? `${ASSISTANT_CHAT_GROUP_KEY_PREFIX}${primaryAssistant.uuid}`
        : UNKNOWN_ASSISTANT_CHAT_GROUP_KEY;
    };

    const key = resolveKey();
    const existingGroup = groupsByKey.get(key);

    if (!existingGroup) {
      groupsByKey.set(key, {
        key,
        assistant: primaryAssistant,
        referenceDate: activityDate,
        chats: [chat],
      });
      return;
    }

    existingGroup.chats.push(chat);

    if (activityDate > existingGroup.referenceDate) {
      existingGroup.referenceDate = activityDate;
    }
  });

  const groups = Array.from(groupsByKey.values());

  groups.forEach((group) => {
    group.chats.sort(
      (chatA, chatB) =>
        getChatActivityDate(chatB).getTime() -
        getChatActivityDate(chatA).getTime(),
    );
  });

  return groups.sort(
    (groupA, groupB) =>
      groupB.referenceDate.getTime() - groupA.referenceDate.getTime(),
  );
};

// NOTE: A resolved group is labelled by its assistant's own name; the two
// assistant-less buckets are told apart by their key, not by a missing name —
// "several assistants" and "the assistant is gone" are different things to say.
export const formatChatAssistantGroupLabel = ({
  group,
  translate,
}: IFormatChatAssistantGroupLabelProps): string => {
  if (group.assistant) return group.assistant.name;

  return translate(
    group.key === MULTI_ASSISTANT_CHAT_GROUP_KEY
      ? MULTI_ASSISTANT_CHAT_GROUP_LABEL_KEY
      : UNKNOWN_ASSISTANT_CHAT_GROUP_LABEL_KEY,
  );
};
